import { Minus, Plus } from "lucide-react";

/**
 * NumberStepper — themed number input with +/- buttons matching the SGM dark/light theme.
 * Hides native browser spinners and provides accessible buttons.
 */
export function NumberStepper({
  value,
  onChange,
  step = 1,
  min,
  max,
  placeholder,
  disabled,
  className = "",
  "data-testid": dataTestId,
  ...rest
}) {
  const parse = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const clamp = (n) => {
    if (typeof min === "number" && n < min) return min;
    if (typeof max === "number" && n > max) return max;
    return n;
  };
  const inc = () => {
    if (disabled) return;
    const next = clamp(parse(value || 0) + step);
    onChange({ target: { value: String(next) } });
  };
  const dec = () => {
    if (disabled) return;
    const next = clamp(parse(value || 0) - step);
    onChange({ target: { value: String(next) } });
  };

  return (
    <div className={`relative flex items-stretch ${className}`}>
      <button
        type="button"
        onClick={dec}
        disabled={disabled}
        aria-label="Disminuir"
        className="flex items-center justify-center w-11 border border-r-0 border-[var(--border-strong)] bg-[var(--bg-surface)] hover:bg-[var(--primary)] hover:border-[var(--primary)] hover:text-[var(--primary-fg)] text-[var(--text-secondary)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        data-testid={dataTestId ? `${dataTestId}-dec` : undefined}
      >
        <Minus className="w-4 h-4" strokeWidth={2.5} />
      </button>
      <input
        type="number"
        inputMode="decimal"
        step={step}
        min={min}
        max={max}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={dataTestId}
        className="no-spinner input-base flex-1 text-center font-mono"
        {...rest}
      />
      <button
        type="button"
        onClick={inc}
        disabled={disabled}
        aria-label="Aumentar"
        className="flex items-center justify-center w-11 border border-l-0 border-[var(--border-strong)] bg-[var(--bg-surface)] hover:bg-[var(--primary)] hover:border-[var(--primary)] hover:text-[var(--primary-fg)] text-[var(--text-secondary)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        data-testid={dataTestId ? `${dataTestId}-inc` : undefined}
      >
        <Plus className="w-4 h-4" strokeWidth={2.5} />
      </button>
    </div>
  );
}
